"use client";

import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { toast } from "sonner";
import {
  EMPTY_MEDIA_UPLOAD_STATE,
  ImageUpload,
  type MediaUploadState,
  type UploadedMedia,
} from "@/components/image-upload";
import { MentionTextarea } from "@/components/mentions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import type { MediaData } from "@/db/schema/post";
import { useCreatePostMutation } from "@/hooks/use-posts";
import { toMediaAssetSlimForWrite } from "@/lib/media";
import { cn } from "@/lib/utils";

type PostCreateContext =
  | {
      type: "hiive";
      hiiveId: number;
    }
  | {
      type: "user";
      userId: string;
    };

interface ViewerInfo {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
}

interface PostCreateProps {
  context: PostCreateContext;
  viewer?: ViewerInfo;
  className?: string;
  placeholder?: string;
  feedLimit?: number;
}

const DEFAULT_FEED_LIMIT = 10;

const renderInitials = (displayName?: string | null) => {
  if (!displayName) return "";
  const parts = displayName.trim().split(" ");
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2)?.toUpperCase() ?? "";
  }
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

export function PostCreate({
  context,
  viewer,
  className,
  placeholder = "Share something...",
  feedLimit = DEFAULT_FEED_LIMIT,
}: PostCreateProps) {
  const [media, setMedia] = useState<UploadedMedia[]>([]);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [mediaUploadState, setMediaUploadState] = useState<MediaUploadState>(
    EMPTY_MEDIA_UPLOAD_STATE,
  );
  const [uploadResetToken, setUploadResetToken] = useState(() =>
    Math.random().toString(36).slice(2),
  );

  const createPostMutation = useCreatePostMutation(feedLimit);

  const form = useForm({
    defaultValues: {
      caption: "",
    },
    onSubmit: async ({ value }) => {
      if (isUploadingMedia) {
        toast.warning("Please wait for uploads to finish.");
        return;
      }

      if (mediaUploadState.hasErrors) {
        toast.error("Remove failed uploads before posting.");
        return;
      }

      const readyMedia = media
        .filter((item) => item.url && !item.error && !item.isUploading)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const rawCaption = String(value.caption ?? "");
      const trimmedCaption = rawCaption.trim();

      if (!trimmedCaption) {
        toast.error("Add a caption before posting.");
        return;
      }

      if (readyMedia.length === 0) {
        toast.error("Attach at least one media item before posting.");
        return;
      }

      const mediaPayload: MediaData[] = readyMedia
        .map((item, index) => toMediaAssetSlimForWrite(item, index))
        .filter((item): item is MediaData => item !== null)
        .map((item, index) => ({ ...item, order: index }));

      createPostMutation.mutate(
        {
          type: context.type,
          entityId: context.type === "hiive" ? context.hiiveId : undefined,
          content: trimmedCaption,
          mediaUrls: mediaPayload,
          locationTag: null,
          contextTag: null,
          visibility: "public",
          status: "published",
          isPinned: false,
          viewer: viewer
            ? {
                id: viewer.id,
                displayName: viewer.displayName,
                username: viewer.username,
                avatarUrl: viewer.avatarUrl,
              }
            : undefined,
        },
        {
          onSuccess: () => {
            toast.success("Post shared");
            form.reset();
            setMedia([]);
            setUploadResetToken(Math.random().toString(36).slice(2));
          },
          onError: (error) => {
            const message =
              error instanceof Error ? error.message : "Failed to create post";
            toast.error(message);
          },
        },
      );
    },
  });

  const disableSubmit =
    createPostMutation.isPending ||
    isUploadingMedia ||
    mediaUploadState.hasErrors;

  const handleClear = () => {
    form.reset();
    setMedia([]);
    setUploadResetToken(Math.random().toString(36).slice(2));
  };

  return (
    <div
      className={cn(
        "rounded-3xl border border-border bg-surface shadow-sm",
        "p-4 sm:p-6",
        className,
      )}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 border border-border">
            <AvatarImage src={viewer?.avatarUrl ?? undefined} />
            <AvatarFallback className="bg-background text-text-primary">
              {renderInitials(viewer?.displayName)}
            </AvatarFallback>
          </Avatar>

          <form.Field name="caption">
            {(field) => (
              <MentionTextarea
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(nextValue) => field.handleChange(nextValue)}
                placeholder={placeholder}
                rows={3}
                disabled={isUploadingMedia || createPostMutation.isPending}
                containerClassName="w-full flex-1"
                className="w-full resize-none rounded-none border-none bg-transparent p-0 text-base text-text-primary focus-visible:ring-0"
              />
            )}
          </form.Field>
        </div>

        <ImageUpload
          key={uploadResetToken}
          maxImages={6}
          onImagesChange={setMedia}
          onUploadingChange={setIsUploadingMedia}
          onUploadStateChange={setMediaUploadState}
          initialImages={[]}
          uploadScope={{ domain: "posts", slot: "media" }}
          className="rounded-2xl border border-dashed border-border bg-surface-alt/60 p-4"
          showReorderHint={false}
          enableCropping
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm text-text-secondary">
            {isUploadingMedia && <span>Uploading media…</span>}
            {!isUploadingMedia && mediaUploadState.hasErrors && (
              <span className="text-error">
                Remove failed uploads before posting.
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-border px-4 text-sm text-text-primary"
              onClick={handleClear}
              disabled={createPostMutation.isPending}
            >
              Clear
            </Button>
            <form.Subscribe selector={(state) => state.values.caption}>
              {(caption) => {
                const trimmedCaption = caption.trim();
                const hasReadyMedia = media.some(
                  (item) => item.url && !item.error && !item.isUploading,
                );
                const canSubmit =
                  !disableSubmit && trimmedCaption.length > 0 && hasReadyMedia;

                return (
                  <Button
                    type="submit"
                    className="rounded-full bg-brand px-6 text-sm font-semibold text-text-inverse hover:bg-brand-hover"
                    disabled={!canSubmit}
                  >
                    {createPostMutation.isPending ? "Sharing…" : "Share"}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </div>
      </form>
    </div>
  );
}
